import React, { useContext, useMemo, useState } from "react";
import styles from "./styles.module.css";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { userLinks } from "./links";
import { AuthContext } from "../../context";
import { FiX, FiLogOut } from "react-icons/fi";

type Props = { pageTitle?: string };

export default function Navigation({}: Props) {
  const { user, open, toggleDrawer, logout } = useContext(AuthContext) as any;
  const navigate = useNavigate();
  const location = useLocation();

  const role = user?.role || "User";

  const links = useMemo(() => {
    const filtered = userLinks.filter(
      (l: any) => !l.allowed || l.allowed.includes(role),
    );
    return filtered.length ? filtered : userLinks; // fallback (so menu never becomes empty)
  }, [role]);

  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    links.forEach((l: any) => {
      if (l.children?.some((c: any) => location.pathname.startsWith(c.route))) {
        map[l.name] = true;
      }
    });
    return map;
  });

  const onLogout = () => {
    logout?.();
    navigate("/");
  };

  const closeOnMobile = () => {
    if (open) toggleDrawer?.();
  };

  return (
    <div className={styles.nav}>
      <div className={styles.top}>
        <div className={styles.brand}>
          <div className={styles.brandMark}>E</div>
          <div className={styles.brandText}>
            <div className={styles.brandName}>Enricher</div>
            <div className={styles.brandSub}>Admin dashboard</div>
          </div>
        </div>

        <button
          type="button"
          className={`${styles.closeBtn} ${open ? styles.closeBtnShow : ""}`}
          onClick={toggleDrawer}
          aria-label="Close menu"
        >
          <FiX />
        </button>
      </div>

      <div className={styles.userBox}>
        <div className={styles.userAvatar}>
          {(user?.firstname?.[0] || "U").toUpperCase()}
        </div>
        <div className={styles.userMeta}>
          <div className={styles.userName}>
            {user?.fullname ||
              [user?.firstname, user?.lastname].filter(Boolean).join(" ") ||
              "User"}
          </div>
          <div className={styles.userRole}>{role}</div>
        </div>
      </div>

      <nav className={styles.menu}>
        {links.map((item: any) => {
          const Icon = item.Icon;

          if (item.children?.length) {
            const isOpen = !!expanded[item.name];
            return (
              <div key={item.name} className={styles.group}>
                <button
                  type="button"
                  className={styles.groupBtn}
                  onClick={() =>
                    setExpanded((p) => ({ ...p, [item.name]: !p[item.name] }))
                  }
                >
                  <div className={styles.groupLeft}>
                    {Icon && <Icon className={styles.icon} />}
                    <span className={styles.groupTitle}>{item.name}</span>
                  </div>
                  <span className={styles.chev}>{isOpen ? "–" : "+"}</span>
                </button>

                {isOpen && (
                  <div className={styles.groupItems}>
                    {item.children.map((c: any) => {
                      const CIcon = c.Icon;
                      return (
                        <NavLink
                          key={c.route}
                          to={c.route}
                          onClick={closeOnMobile}
                          className={({ isActive }) =>
                            isActive
                              ? `${styles.link} ${styles.active}`
                              : styles.link
                          }
                        >
                          {CIcon && <CIcon className={styles.subIcon} />}
                          <span>{c.name}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          // normal link (no children)
          return (
            <NavLink
              key={item.route}
              to={item.route}
              onClick={closeOnMobile}
              className={({ isActive }) =>
                isActive ? `${styles.link} ${styles.active}` : styles.link
              }
            >
              {Icon && <Icon className={styles.icon} />}
              <span>{item.name}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className={styles.bottom}>
        <button className={styles.logoutBtn} onClick={onLogout}>
          <FiLogOut /> Logout
        </button>
      </div>
    </div>
  );
}
